import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { LineChart, ChevronDown, TrendingUp, TrendingDown, Minus } from "lucide-react";

export function CoachHistory({ refreshKey }) {
  const [h, setH] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/warmup/coach/history").then(r => setH(r.data)).catch(() => {}); }, [refreshKey]);
  if (!h || h.stats.total === 0) return null;
  const s = h.stats;
  const Trend = s.trend > 0 ? TrendingUp : s.trend < 0 ? TrendingDown : Minus;
  const trendColor = s.trend > 0 ? "text-emerald-400" : s.trend < 0 ? "text-amber-400" : "text-slate-400";

  return (
    <div className="aria-card p-5 mb-6" data-testid="coach-history">
      <button onClick={() => setOpen(!open)} data-testid="coach-history-toggle" className="w-full flex items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <LineChart size={15} className="text-[var(--aria-primary-glow)]" />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">Clarity over time</span>
          <span className="aria-pill text-[10px]">{s.total} take{s.total === 1 ? "" : "s"}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className={`inline-flex items-center gap-1 ${trendColor}`} data-testid="coach-trend"><Trend size={12} /> {s.trend > 0 ? `+${s.trend}` : s.trend ?? "—"} since first take</span>
          <span className="text-slate-400">latest <b className="text-white" data-testid="coach-latest">{s.latest}</b>/10 · best {s.best} · week avg {s.week_avg ?? "—"}</span>
          <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      <Sparkline series={h.series} />
      {open && (
        <div className="mt-4 space-y-2 fade-in" data-testid="coach-takes">
          {h.takes.slice(0, 10).map((t, i) => (
            <div key={i} className="rounded-md bg-white/5 border border-white/10 p-3 text-sm" data-testid={`coach-take-${i}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-display">{t.concept || "General"}</span>
                <span className="font-mono text-xs text-slate-300">{t.original_clarity ?? "—"} → <b className={t.retake_clarity >= (t.original_clarity ?? 0) ? "text-emerald-400" : "text-amber-400"}>{t.retake_clarity ?? "—"}</b>/10 · {new Date(t.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              </div>
              <div className="text-xs text-slate-400 line-clamp-2">{t.verdict}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Sparkline({ series }) {
  if (!series?.length) return null;
  const w = 600, h = 64, pad = 6;
  const xs = series.map((_, i) => pad + (i / Math.max(1, series.length - 1)) * (w - pad * 2));
  const ys = series.map(p => h - pad - ((p.score - 1) / 9) * (h - pad * 2));
  const d = xs.map((x, i) => `${i ? "L" : "M"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16 mt-3" data-testid="coach-sparkline">
      <defs><linearGradient id="cg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="#4F8CFF" stopOpacity="0.35" /><stop offset="100%" stopColor="#4F8CFF" stopOpacity="0" /></linearGradient></defs>
      {[3, 5, 7, 9].map(v => <line key={v} x1={pad} x2={w - pad} y1={h - pad - ((v - 1) / 9) * (h - pad * 2)} y2={h - pad - ((v - 1) / 9) * (h - pad * 2)} stroke="rgba(255,255,255,0.06)" />)}
      {series.length > 1 && <path d={`${d} L${xs[xs.length - 1]},${h - pad} L${xs[0]},${h - pad} Z`} fill="url(#cg)" />}
      <path d={d} fill="none" stroke="#4F8CFF" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {xs.map((x, i) => <circle key={i} cx={x} cy={ys[i]} r={series.length > 30 ? 2 : 3.5} fill="#030711" stroke={i === xs.length - 1 ? "#34d399" : "#4F8CFF"} strokeWidth="2"><title>{series[i].concept}: {series[i].score}/10</title></circle>)}
    </svg>
  );
}
